import {
  Job,
  LocaleEnum,
  QueryRecommendationsArgs,
  QuerySearchJobsArgs,
  Readable,
  RecommendationsQueryVariables,
  SearchableEnum,
  useRecommendationsLazyQuery,
  useSearchJobsLazyQuery,
} from 'generated/apolloComponents'
import React, { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import useTranslation from 'next-translate/useTranslation'
import Script from 'next/script'
import dynamic from 'next/dynamic'
import { useAnalyticsPush } from '@/context/analyticsContext'
import Comments from '@/components/organisms/Comments/Comments'
import { formatDate, getReadingTime } from '@/helpers/utils'
import Time from '@/components/atoms/Time'
import NewsDate from '@/components/atoms/NewsDate'
import Avatar from '@/components/atoms/Avatar'
import { useMe } from '@/context/meContext'
import FacebookIcon from '@/components/atoms/SocialIcons/FacebookIcon'
import LinkedinIcon from '@/components/atoms/SocialIcons/LinkedinIcon'
import TwitterIcon from '@/components/atoms/SocialIcons/TwitterIcon'
import { useCountry } from '@/context/countryContext'
import Content from './Content'
// import Banner from '@/components/molecules/Banner' // Not used in production

interface IProps {
  readable: Readable
}

const SingleReadable = ({ readable }: IProps) => {
  const articleDate = formatDate(
    readable.updated && readable.updated > readable.publishedAt
      ? readable.updated
      : readable.publishedAt,
    'pl-PL'
  )

  const twoImage = () => readable.author?.photo && readable.author?.companyLogo

  const authorLink = () =>
    readable.author?.kind === 'organization'
      ? `/companies/profiles/${readable.author?.id}`
      : `/readme/authors/${readable.author?.id}`

  const readingTime = getReadingTime(Number(readable.wordCount))
  const [ssrWindow, setSsrWindow] = useState()
  const [offers, setOffers] = useState<Job[]>()
  const { me, loadMe } = useMe()
  const { pushEvent } = useAnalyticsPush()
  const { t, lang } = useTranslation('common')
  const { country } = useCountry()

  const DynamicRelatedJobs = dynamic(
    () => import('../../molecules/ReadableRelatedJobsList'),
    { ssr: false }
  )
  const DynamicRelatedReadables = dynamic(
    () => import('../../molecules/ReadableRelatedReadableList'),
    { ssr: false }
  )
  const DynamicHighlight = dynamic(
    () => import('../../molecules/ReadableHighlight'),
    { ssr: false }
  )

  const variables: QueryRecommendationsArgs = {
    perPage: 5,
    page: 1,
    language: LocaleEnum[lang as 'pl' | 'en'],
    resourceId: readable.id,
    resourceType: SearchableEnum.Readable,
    returnType: SearchableEnum.Job,
    boostWhere: {
      type: 'with_salary',
      value: 'true',
      factor: 500,
    },
  }

  const [recommendationsQuery] = useRecommendationsLazyQuery({
    onCompleted: (data) => {
      if (data && data.recommendations) {
        setOffers(data.recommendations as Job[])
      }
    },
  })
  const [searchJobsQuery] = useSearchJobsLazyQuery({
    onCompleted: (data) => {
      if (data && data.searchJobs.nodes && data.searchJobs.nodes.length > 0) {
        setOffers(data.searchJobs.nodes as Job[])
      } else {
        recommendationsQuery({
          variables: variables as RecommendationsQueryVariables,
        })
      }
    },
  })

  const fetchRecommendations = useCallback(async () => {
    if (me && me.rejectedJobs) variables.exclude = [...me.rejectedJobs]
    if (readable.company) {
      const companyVariables: QuerySearchJobsArgs = {
        page: 1,
        perPage: 5,
        language: LocaleEnum[lang as 'pl' | 'en'],
        filters: {
          company: [readable.company?.id.split('-')[0]],
        },
        boostWhere: readable.mainTag
          ? {
              type: 'technologies',
              value: readable.mainTag,
              factor: 500,
            }
          : null,
      }
      if (me && me.rejectedJobs) companyVariables.exclude = [...me.rejectedJobs]
      searchJobsQuery({ variables: companyVariables })
    } else {
      recommendationsQuery({
        variables: variables as RecommendationsQueryVariables,
      })
    }
  }, [readable, me])

  useEffect(() => {
    const windowLocation: any = window.location
    setSsrWindow(windowLocation)
  }, [])

  useEffect(() => {
    loadMe()
    fetchRecommendations()
  }, [fetchRecommendations])

  useEffect(() => {
    if (window) {
      pushEvent({
        event: 'viewed',
        articleData: {
          id: readable.title,
          type: readable.readableType,
          title: readable.title,
          technology: readable.mainTag,
          role: readable.roles,
          category:
            readable.categories &&
            readable.categories?.map((item, index) => item.key),
          seniority: readable.experiences,
          tier: readable.tier,
          forIT: true,
          language: readable.language,
        },
      })
    }
  }, [readable])

  // const shouldShowBanner = !readable.company

  return (
    <>
      {/* {shouldShowBanner && <Banner />} */}
      <main className='mt-14 mb-24'>
        <article className='container container-medium article'>
          <div className='flex flex-col md:flex-row'>
            <div
              className={`md:w-60 md:mr-10 order-2 md:order-1 ${
                twoImage() ? '' : 'flex md:block gap-2 mt-3 items-center'
              }`}
            >
              <div
                className={`${
                  twoImage() ? 'mt-5 md:mt-0' : ''
                } flex items-center justify-center gap-2`}
              >
                {readable.author?.photo && (
                  <div>
                    <a href={authorLink()}>
                      <Avatar
                        url={readable.author?.photo.url}
                        size='normal'
                        alt={readable.author?.name as string}
                      />
                    </a>
                  </div>
                )}

                {readable.author?.companyLogo && (
                  <div>
                    <a href={authorLink()}>
                      <img
                        width='1'
                        height='1'
                        alt={readable.author.company as string}
                        src={readable.author.companyLogo.url ?? ''}
                        className='max-h-[48px] w-full object-contain'
                      />
                    </a>
                  </div>
                )}
              </div>
              <div
                className={`${
                  twoImage() ? 'mt-3 text-center' : 'md:text-center md:mt-3'
                }`}
              >
                {readable.author?.name ? (
                  <>
                    <span className='block md:ml-0 text-xl '>
                      {readable.author?.name}
                    </span>
                    <span className='block text-xs'>
                      {[readable.author?.position, readable.author?.company]
                        .filter((elm) => elm)
                        .join(' @ ')}
                    </span>
                  </>
                ) : (
                  <span className='block md:ml-0 text-xl '>
                    {readable.author?.company}
                  </span>
                )}
              </div>
            </div>
            <div className='flex-1 order-1 md:order-2'>
              <h1 className='font-bold md:text-6xl'>{readable.title}</h1>
              <div className='font-normal text-base md:text-3xl leading-7 md:leading-normal mt-8'>
                {readable.intro}
              </div>
              <div className='flex gap-4 mt-3'>
                <NewsDate date={articleDate} className='inline-block' />
                <Time time={readingTime} className='inline-block' />
              </div>
            </div>
          </div>
          <div className='mt-6 md:mt-10'>
            {readable.categories &&
              readable.categories?.map((item, index) => (
                <Link
                  href={`/readme/s/categories,${item.key}`}
                  key={index}
                  prefetch={false}
                  className={` mr-2 mb-3 inline-block rounded-4xl border  px-6 py-1.5 hover:bg-gray-100 border-gray-100`}
                >
                  {item.titles && item.titles[lang as 'pl' | 'en']}
                </Link>
              ))}
          </div>
          <div className='mt-6 md:mt-10 mb-6 md:mb-20'>
            {readable.cover?.url && (
              <div className='relative w-full'>
                <img
                  className='w-full top-0'
                  src={readable.cover?.url}
                  alt={readable.title!}
                  width='1200'
                  height='628'
                />
              </div>
            )}
          </div>
          <div className='flex flex-col md:flex-row relative'>
            <div className='md:mr-10 order-2 md:order-1 w-60'>
              <div
                className='hidden md:block md:sticky mt-10 md:mt-0'
                style={{ top: '80px' }}
              >
                {ssrWindow && (
                  <ul className='flex justify-around sm:justify-items-start md:block'>
                    <li className='px-4 md:px-0 md:mb-8'>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${ssrWindow}`}
                        title={t('common:share_facebook')}
                        target='_blank'
                        rel='noreferrer'
                      >
                        <FacebookIcon width={32} />
                      </a>
                    </li>
                    <li className='px-4 md:px-0 md:mb-8'>
                      <a
                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${ssrWindow}`}
                        target='_blank'
                        title={t('common:share_linkedin')}
                        rel='noreferrer'
                      >
                        <LinkedinIcon width={32} />
                      </a>
                    </li>
                    <li className='px-4 md:px-0'>
                      <a
                        href={`http://twitter.com/share?text=${encodeURIComponent(
                          readable.title as string
                        )}&url=${ssrWindow}`}
                        target='_blank'
                        title={t('common:share_twitter')}
                        rel='noreferrer'
                      >
                        <TwitterIcon width={32} />
                      </a>
                    </li>
                  </ul>
                )}
              </div>
            </div>

            <Content
              readable={readable}
              offers={offers}
              className='flex-1 min-w-0'
            />
          </div>
        </article>
        <div className='container-medium mt-14'>
          <div className='md:px-40'>
            <Comments readable={readable} />
          </div>
        </div>
        <div className='container'>
          <section className='mt-14 md:mt-24'>
            <DynamicRelatedJobs
              readable={readable}
              locale={lang as LocaleEnum}
              country={country}
            />
          </section>
          <DynamicRelatedReadables
            readable={readable}
            locale={lang as LocaleEnum}
            perPage={3}
          />
        </div>

        <DynamicHighlight />
        <Script
          strategy='afterInteractive'
          dangerouslySetInnerHTML={{
            __html: `
            var loadImg = function() {
              for(const img of document.querySelectorAll("img[data-src]")) {
                const url = img.getAttribute('data-src');
                const newImg = new Image();
                newImg.onload = function() {
                  img.src = url;
                };
                newImg.src = url;
              }
            };
            loadImg();
          `,
          }}
        />
      </main>
    </>
  )
}

export default SingleReadable
